/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { map, Observable } from 'rxjs';
import { DialogService } from 'nebula-ui-kit';
import { inject, Injectable } from '@angular/core';
import { SelectDialogComponent } from '../../../select-dialog/select-dialog.component';
import { CostCenterService, EstadoCentroCostos } from './centro-costos.service';
import { TranslationService, TREASURY } from '@nebula-shared/data-access';
import { ordenarEnMemoria } from '../../../select-dialog/orden-dialogo';

export interface CentroCostoSelect {
  id: number;
  codigo: string;
  nombre: string;
  idTipoCentroCostos?: number;
  nombreTipoCentroCostos?: string;
  /** Estado de negocio del centro (R-E02). El selector solo ofrece los ACTIVO. */
  estadoCentroCostos?: EstadoCentroCostos;
}

/** Filtros del selector. Los centros que ofrece son siempre los activos: los trae asi el servidor. */
export interface CentroCostoFilters {
  tipoCentroCostoId?: number;
}

@Injectable({ providedIn: 'root' })
export class CentroCostoModalService {
  readonly dialogService = inject(DialogService);
  readonly costCenterService = inject(CostCenterService);
  readonly translation = inject(TranslationService);

  protected t(key: string, params?: any): string {
    return this.translation.translate(key, params);
  }

  openSelector(
    title: string,
    currentValue?: string,
    filters?: CentroCostoFilters,
    preloadedCentros?: CentroCostoSelect[],
  ): Observable<CentroCostoSelect | null> {
    const loadDataFn = this.createLoadDataFn(filters, preloadedCentros);

    const dialogRef = this.dialogService.open(SelectDialogComponent, {
      data: {
        title: title,
        columns: this.getColumns(),
        multiSelect: false,
        searchPlaceholder: this.t('TREASURY.COMMON.MODALS.SEARCH.COST_CENTER.PLACEHOLDER'),
        loadDataFn: loadDataFn,
        itemIdKey: 'id',
        displayKey: 'codigo',
        selectedItems: currentValue ? [{ codigo: currentValue, nombre: currentValue }] : [],
        pageSize: 10,
        pageSizeOptions: [10, 25, 50, 100],
      },
      size: 'lg',
      width: '800px',
    });

    return dialogRef
      .afterClosed()
      .pipe(map((selected: CentroCostoSelect[]) => selected?.[0] || null));
  }

  private getColumns() {
    return [
      {
        key: 'codigo',
        label: this.t('TREASURY.COMMON.MODALS.COST_CENTER.COLUMNS.CODE'),
        sortable: true,
        width: '30%',
      },
      {
        key: 'nombre',
        label: this.t('TREASURY.COMMON.MODALS.COST_CENTER.COLUMNS.NAME'),
        sortable: true,
        width: '50%',
      },
      {
        key: 'nombreTipoCentroCostos',
        label: this.t('TREASURY.COMMON.MODALS.COST_CENTER.COLUMNS.TYPE'),
        sortable: true,
        width: '20%',
      },
    ];
  }

  private createLoadDataFn(filters?: CentroCostoFilters, preloadedCentros?: CentroCostoSelect[]) {
    return async (params: any): Promise<{ data: CentroCostoSelect[]; total: number }> => {
      const page = params.page - 1;
      const size = params.limit || 10;
      const searchTerm = params.search || '';

      try {
        let rawCentros: any[] = [];

        if (preloadedCentros && preloadedCentros.length > 0) {
          rawCentros = preloadedCentros;
        } else if (filters?.tipoCentroCostoId) {
          rawCentros =
            (await this.costCenterService
              .getCentrosByTipo(filters.tipoCentroCostoId)
              .toPromise()) || [];
        } else {
          rawCentros = (await this.costCenterService.getActiveCentros().toPromise()) || [];
        }

        const centros: CentroCostoSelect[] = rawCentros.map((c: any) => ({
          id: c.id,
          codigo: c.codigo,
          nombre: c.nombre,
          idTipoCentroCostos: c.idTipoCentroCostos ?? c.tipoCentroCostoId,
          nombreTipoCentroCostos: c.nombreTipoCentroCostos ?? c.tipoCentroCostoNombre,
          estadoCentroCostos: c.estadoCentroCostos,
        }));

        let filteredCentros = centros;

        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          filteredCentros = filteredCentros.filter(
            (c) => c.codigo?.toLowerCase().includes(term) || c.nombre?.toLowerCase().includes(term),
          );
        }

        const start = page * size;
        const end = start + size;
        // El orden elegido se aplica a la lista completa antes de cortar la pagina (Mantis #392).
        const paginatedData = ordenarEnMemoria(filteredCentros, params).slice(start, end);

        return {
          data: paginatedData,
          total: filteredCentros.length,
        };
      } catch (error) {
        return { data: [], total: 0 };
      }
    };
  }
}
